/**
 * Course catalog for the 2026/27 intake (EN + JA).
 * Used by: header mega menu, Courses page, course detail pages (/courses/[slug]),
 * home page course showcase and the footer.
 * To add a course: copy one block, give it a new unique `slug`, and rebuild.
 * Durations are typical lengths; the team confirms exact dates and fees on enquiry.
 */
export type Locale = 'en' | 'ja'
export type CategoryId = 'language' | 'exam' | 'career' | 'professional' | 'kids'
type T = { en: string; ja: string }
type TL = { en: string[]; ja: string[] }

export interface Course {
  slug: string
  category: CategoryId
  /** lucide icon name, mapped in components/course/CourseIcon.tsx */
  icon: string
  /** big Japanese characters shown on the card */
  kanji: string
  title: T
  /** Japanese name shown under the title on English pages */
  jpName: string
  short: T
  level: T
  duration: T
  overview: T
  learn: TL
  forWho: TL
  outcome: T
  /** show the JLPT batch timetable on the detail page */
  batches?: boolean
  featured?: boolean
}

export const INTAKE = { en: '2026/27 Intake', ja: '2026/27年度 入学' }
export const MODES = { en: 'On campus · Online', ja: '通学・オンライン' }

/** Same class times as the Individuals page */
export const BATCHES = [
  { en: 'Morning batch', ja: '午前クラス', time: '9:00 – 11:00 AM' },
  { en: 'Afternoon batch', ja: '午後クラス', time: '2:00 – 4:00 PM' },
]

export const CATEGORIES: { id: CategoryId; title: T; jp: string; blurb: T }[] = [
  {
    id: 'language',
    title: { en: 'Japanese Language', ja: '日本語' },
    jp: '日本語',
    blurb: { en: 'From your first hiragana to fluent conversation.', ja: 'ひらがなから自然な会話まで。' },
  },
  {
    id: 'exam',
    title: { en: 'Exam Preparation', ja: '試験対策' },
    jp: '試験対策',
    blurb: { en: 'JLPT N5 to N1 and JFT-Basic, with mock tests.', ja: 'JLPT N5〜N1とJFT-Basicを模試付きで。' },
  },
  {
    id: 'career',
    title: { en: 'Work in Japan', ja: '日本で働く' },
    jp: '就労準備',
    blurb: { en: 'SSW, caregiving, TITP and interview preparation.', ja: '特定技能・介護・技能実習・面接対策。' },
  },
  {
    id: 'professional',
    title: { en: 'Business & Professional', ja: 'ビジネス・専門' },
    jp: 'ビジネス',
    blurb: { en: 'Business Japanese, IT Japanese, translation.', ja: 'ビジネス日本語・IT日本語・翻訳通訳。' },
  },
  {
    id: 'kids',
    title: { en: 'Kids & Culture', ja: 'キッズ・文化' },
    jp: '文化',
    blurb: { en: 'Japanese for ages 9–12 and culture workshops.', ja: '9〜12歳の日本語と文化体験。' },
  },
]

const jlpt = (
  n: 5 | 4 | 3 | 2 | 1,
  months: number,
  kanji: string,
  vocab: string,
  levelEn: string,
  levelJa: string,
  learnEn: string[],
  learnJa: string[],
  outcomeEn: string,
  outcomeJa: string
): Course => ({
  slug: `jlpt-n${n}`,
  category: 'exam',
  icon: 'GraduationCap',
  kanji: `N${n}`,
  title: { en: `JLPT N${n} Preparation`, ja: `JLPT N${n} 対策コース` },
  jpName: `日本語能力試験 N${n} 対策`,
  short: {
    en: `${levelEn}. About ${kanji} kanji and ${vocab} words, with weekly mock tests.`,
    ja: `${levelJa}。漢字約${kanji}字・語彙約${vocab}語、毎週の模擬試験付き。`,
  },
  level: { en: levelEn, ja: levelJa },
  duration: { en: `About ${months} months`, ja: `約${months}か月` },
  overview: {
    en: `A structured JLPT N${n} course that covers all three test sections: vocabulary & kanji, grammar & reading, and listening. Classes run five days a week in small groups, with timed mock tests so you walk into the exam knowing the format.`,
    ja: `JLPT N${n}の3つの試験科目（文字・語彙、文法・読解、聴解）をすべて扱う体系的なコースです。少人数で週5日授業を行い、時間を計った模擬試験で本番の形式に慣れていきます。`,
  },
  learn: { en: learnEn, ja: learnJa },
  forWho: {
    en: [
      n === 5 ? 'Complete beginners (we start from hiragana)' : `Students who have finished N${n + 1} or have equivalent knowledge`,
      'Anyone planning to study or work in Japan',
      'Learners who want a recognised certificate',
    ],
    ja: [
      n === 5 ? 'まったくの初心者（ひらがなから始めます）' : `N${n + 1}を修了した方、または同等の力がある方`,
      '日本での留学・就職を考えている方',
      '公式な資格を取りたい方',
    ],
  },
  outcome: { en: outcomeEn, ja: outcomeJa },
  batches: true,
  featured: n === 5 || n === 4,
})

export const COURSES: Course[] = [
  // ---------------- Japanese language
  {
    slug: 'japanese-foundations',
    category: 'language',
    icon: 'Languages',
    kanji: 'あ',
    title: { en: 'Japanese Foundations: Hiragana & Katakana', ja: '日本語入門：ひらがな・カタカナ' },
    jpName: 'ひらがな・カタカナ入門',
    short: {
      en: 'Read and write both Japanese alphabets, plus your first greetings and phrases.',
      ja: '2種類の文字の読み書きと、最初のあいさつ・フレーズ。',
    },
    level: { en: 'Absolute beginner', ja: '入門' },
    duration: { en: 'About 4 weeks', ja: '約4週間' },
    overview: {
      en: 'The fastest way to start. In four weeks you will read and write all 92 basic hiragana and katakana, pronounce Japanese correctly and introduce yourself. It leads straight into JLPT N5.',
      ja: '最短で日本語を始めるコースです。4週間でひらがな・カタカナ全92字の読み書き、正しい発音、自己紹介ができるようになります。そのままJLPT N5へ進めます。',
    },
    learn: {
      en: ['All 46 hiragana and 46 katakana', 'Correct Japanese pronunciation and rhythm', 'Greetings, numbers, days and times', 'Self-introduction (jikoshoukai)'],
      ja: ['ひらがな46字・カタカナ46字', '正しい発音とリズム', 'あいさつ・数字・曜日・時間', '自己紹介'],
    },
    forWho: {
      en: ['Complete beginners', 'Students joining the next JLPT N5 batch', 'Anyone curious about Japanese'],
      ja: ['まったくの初心者', '次のN5クラスに参加する方', '日本語に興味がある方'],
    },
    outcome: { en: 'Read and write both alphabets and start JLPT N5 with confidence.', ja: '2種類の文字を読み書きでき、自信を持ってN5を始められます。' },
    featured: true,
  },
  {
    slug: 'spoken-japanese',
    category: 'language',
    icon: 'MessagesSquare',
    kanji: '話',
    title: { en: 'Spoken Japanese & Conversation', ja: '日本語会話コース' },
    jpName: '日本語会話',
    short: { en: 'Speak from day one: role-plays, listening and real-life situations.', ja: '初日から話す：ロールプレイ・聴解・実生活の場面。' },
    level: { en: 'Beginner to intermediate', ja: '初級〜中級' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'A speaking-first course built around situations you will actually meet: shops, trains, the workplace, the doctor and making friends. Lots of pair work, recordings and feedback on pronunciation.',
      ja: '買い物、電車、職場、病院、友だちづくりなど、実際に出会う場面を中心にした「話す」コースです。ペア練習、録音、発音フィードバックがたくさんあります。',
    },
    learn: {
      en: ['Everyday conversations and polite forms', 'Listening to natural-speed Japanese', 'Phone calls, directions and shopping', 'Small talk at work and with neighbours'],
      ja: ['日常会話と丁寧な言い方', '自然な速さの日本語の聞き取り', '電話・道案内・買い物', '職場や近所での雑談'],
    },
    forWho: { en: ['Learners who can read hiragana', 'JLPT students who want to speak more', 'People moving to Japan soon'], ja: ['ひらがなが読める方', 'もっと話したいJLPT学習者', 'まもなく日本へ行く方'] },
    outcome: { en: 'Hold everyday conversations in Japanese with confidence.', ja: '日常会話を自信を持ってこなせます。' },
  },
  {
    slug: 'kanji-mastery',
    category: 'language',
    icon: 'PenTool',
    kanji: '漢',
    title: { en: 'Kanji Mastery', ja: '漢字マスター' },
    jpName: '漢字集中講座',
    short: { en: 'Learn kanji by radicals and stories, not by memorising lists.', ja: '部首とストーリーで覚える、丸暗記しない漢字学習。' },
    level: { en: 'N5 to N2', ja: 'N5〜N2' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'Kanji is where most learners get stuck. This course teaches the building blocks (radicals), stroke order and memory techniques, then practises reading kanji in real signs, forms and job notices.',
      ja: '多くの学習者がつまずく漢字。部首・書き順・記憶法を学び、看板や書類、求人票など実際の場面で読む練習をします。',
    },
    learn: { en: ['Radicals and stroke order', 'Memory techniques for 300+ kanji', 'Reading signs, forms and notices', 'Kanji for your JLPT level'], ja: ['部首と書き順', '300字以上を覚える記憶法', '看板・書類・お知らせを読む', 'JLPTレベル別の漢字'] },
    forWho: { en: ['JLPT students', 'Anyone who finds kanji hard', 'Workers who need to read at work'], ja: ['JLPT受験者', '漢字が苦手な方', '仕事で読む必要がある方'] },
    outcome: { en: 'Read and remember kanji faster, with a method that keeps working.', ja: '続けられる方法で、漢字を速く読み、覚えられます。' },
  },

  // ---------------- Exams
  jlpt(5, 4, '100', '800', 'Beginner', '初級',
    ['Hiragana, katakana and about 100 kanji', 'Basic grammar and polite sentences', 'Reading short everyday texts', 'Listening to slow, simple conversations', 'Weekly timed mock tests'],
    ['ひらがな・カタカナ・漢字約100字', '基本文法と丁寧な文', '短い日常文の読解', 'ゆっくりした簡単な会話の聴解', '毎週の時間制模擬試験'],
    'Pass JLPT N5 and understand basic everyday Japanese.', 'JLPT N5に合格し、基本的な日常の日本語が理解できます。'),
  jlpt(4, 5, '300', '1,500', 'Elementary', '初中級',
    ['About 300 kanji and 1,500 words', 'Te-form, plain form and common grammar', 'Reading notices and short messages', 'Everyday listening at natural pace', 'Weekly timed mock tests'],
    ['漢字約300字・語彙約1,500語', 'て形・普通形などの基本文法', 'お知らせや短いメッセージの読解', '自然な速さの日常会話の聴解', '毎週の時間制模擬試験'],
    'Pass JLPT N4, the level most SSW employers ask for.', '特定技能の多くの企業が求めるJLPT N4に合格できます。'),
  jlpt(3, 6, '650', '3,750', 'Intermediate', '中級',
    ['About 650 kanji and 3,750 words', 'Intermediate grammar and expressions', 'Reading articles and instructions', 'Understanding conversations at work', 'Weekly timed mock tests'],
    ['漢字約650字・語彙約3,750語', '中級の文法・表現', '記事や説明文の読解', '職場の会話の理解', '毎週の時間制模擬試験'],
    'Pass JLPT N3 and work comfortably in a Japanese-speaking team.', 'JLPT N3に合格し、日本語のチームで安心して働けます。'),
  jlpt(2, 8, '1,000', '6,000', 'Upper intermediate', '中上級',
    ['About 1,000 kanji and 6,000 words', 'Advanced grammar for business and news', 'Reading newspapers and long texts', 'Listening to meetings and broadcasts', 'Weekly timed mock tests'],
    ['漢字約1,000字・語彙約6,000語', 'ビジネスやニュースの上級文法', '新聞や長文の読解', '会議や放送の聴解', '毎週の時間制模擬試験'],
    'Pass JLPT N2, the common requirement for office jobs and universities in Japan.', '日本の事務職や大学で多く求められるJLPT N2に合格できます。'),
  jlpt(1, 10, '2,000', '10,000', 'Advanced', '上級',
    ['About 2,000 kanji and 10,000 words', 'Nuanced, formal and written grammar', 'Editorials, essays and specialised texts', 'Lectures and fast natural speech', 'Weekly timed mock tests'],
    ['漢字約2,000字・語彙約10,000語', '細かなニュアンス・書き言葉の文法', '社説・論説・専門的な文章', '講義や速い自然な話し言葉', '毎週の時間制模擬試験'],
    'Pass JLPT N1, the highest level, valued for professional and specialist roles.', '最上級のJLPT N1に合格し、専門職でも評価されます。'),
  {
    slug: 'jft-basic',
    category: 'exam',
    icon: 'ClipboardCheck',
    kanji: 'JFT',
    title: { en: 'JFT-Basic Preparation (A2)', ja: 'JFT-Basic 対策（A2）' },
    jpName: '国際交流基金日本語基礎テスト 対策',
    short: { en: 'The computer-based Japanese test accepted for the SSW visa.', ja: '特定技能ビザで認められるコンピューター試験。' },
    level: { en: 'Beginner (A2)', ja: '初級（A2）' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'The Japan Foundation Test for Basic Japanese (JFT-Basic) is a computer-based test held many times a year. A result at A2 level meets the Japanese-language requirement for the Specified Skilled Worker (i) visa, alongside the skills test for your field. We train you on the real test format on screen.',
      ja: '国際交流基金日本語基礎テスト（JFT-Basic）は年に何度も実施されるコンピューター試験です。A2レベルの結果は、分野別の技能試験とあわせて特定技能1号の日本語要件を満たします。実際の画面形式で練習します。',
    },
    learn: { en: ['Script and vocabulary', 'Conversation and expression', 'Listening and reading comprehension', 'Practice on the computer-based format'], ja: ['文字と語彙', '会話と表現', '聴解と読解', 'コンピューター形式での練習'] },
    forWho: { en: ['Candidates for the SSW visa', 'Workers who need a fast, frequent test', 'N5 graduates'], ja: ['特定技能ビザの希望者', '早く受験したい方', 'N5修了者'] },
    outcome: { en: 'Reach A2 on JFT-Basic for your SSW application.', ja: '特定技能申請のためにJFT-BasicでA2に到達します。' },
    featured: true,
  },

  // ---------------- Work in Japan
  {
    slug: 'ssw-preparation',
    category: 'career',
    icon: 'Briefcase',
    kanji: '特',
    title: { en: 'Specified Skilled Worker (SSW) Preparation', ja: '特定技能 試験対策' },
    jpName: '特定技能 試験・就労準備',
    short: { en: 'Japanese + skills-test preparation for your SSW field.', ja: '分野別の技能試験と日本語をセットで準備。' },
    level: { en: 'N5 / A2 and up', ja: 'N5・A2以上' },
    duration: { en: 'About 4–6 months', ja: '約4〜6か月' },
    overview: {
      en: 'Specified Skilled Worker (i) lets you work in Japan in fields such as nursing care, food service, agriculture, construction, manufacturing and accommodation. You need two things: Japanese at JLPT N4 or JFT-Basic A2, and the skills test for your field. This course prepares both, plus interviews and life in Japan.',
      ja: '特定技能1号では、介護・外食・農業・建設・製造・宿泊などの分野で日本で働けます。必要なのは、JLPT N4またはJFT-Basic A2の日本語と、分野別の技能試験の2つ。このコースは両方に加え、面接と日本での生活まで準備します。',
    },
    learn: { en: ['Japanese for your chosen field', 'Skills-test question practice', 'Workplace safety and rules in Japan', 'Interview practice and CV in Japanese'], ja: ['分野別の日本語', '技能試験の問題演習', '日本の職場の安全とルール', '面接練習と日本語の履歴書'] },
    forWho: { en: ['Job seekers aiming for Japan', 'Workers with experience in a SSW field', 'JLPT N5/N4 students'], ja: ['日本での就職を目指す方', '特定技能分野の経験者', 'JLPT N5・N4の学習者'] },
    outcome: { en: 'Be ready for the Japanese and skills tests needed for the SSW (i) visa.', ja: '特定技能1号に必要な日本語試験と技能試験の準備が整います。' },
    featured: true,
  },
  {
    slug: 'caregiving-japanese',
    category: 'career',
    icon: 'HeartHandshake',
    kanji: '介',
    title: { en: 'Caregiving (Kaigo) Japanese', ja: '介護の日本語' },
    jpName: '介護日本語・介護技能',
    short: { en: 'The Japanese used in nursing care, plus the Kaigo language test.', ja: '介護現場の日本語と介護日本語評価試験。' },
    level: { en: 'N5 / A2 and up', ja: 'N5・A2以上' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'Nursing care is one of the largest SSW fields. This course teaches the words and phrases carers use every day (body parts, meals, bathing, moving residents, reporting to staff), and prepares you for the Nursing Care Japanese Language Evaluation Test and the care skills test.',
      ja: '介護は特定技能で最も大きな分野の一つです。体の部位、食事、入浴、移動介助、報告など、毎日使う言葉と表現を学び、介護日本語評価試験と介護技能評価試験に備えます。',
    },
    learn: { en: ['Care vocabulary and polite speech to elders', 'Reporting and recording (hou-ren-sou)', 'Safe-care basics and hygiene', 'Mock Kaigo language and skills tests'], ja: ['介護の語彙と高齢者への丁寧な話し方', '報告・連絡・相談と記録', '安全な介護の基本と衛生', '介護日本語・技能の模擬試験'] },
    forWho: { en: ['Future carers in Japan', 'Nursing and health students', 'SSW nursing-care candidates'], ja: ['日本で介護職を目指す方', '看護・保健を学ぶ学生', '特定技能（介護）の希望者'] },
    outcome: { en: 'Communicate safely and kindly in a Japanese care home.', ja: '日本の介護施設で安全に、思いやりを持ってコミュニケーションできます。' },
  },
  {
    slug: 'titp-pre-departure',
    category: 'career',
    icon: 'PlaneTakeoff',
    kanji: '渡',
    title: { en: 'TITP & Pre-departure Training', ja: '技能実習・出国前研修' },
    jpName: '技能実習・育成就労 出国前研修',
    short: { en: 'Language, rules and daily life before you fly to Japan.', ja: '渡航前に日本語・ルール・生活を学ぶ。' },
    level: { en: 'Beginner', ja: '初級' },
    duration: { en: 'About 4 months', ja: '約4か月' },
    overview: {
      en: 'Intensive preparation for trainees going to Japan under the Technical Intern Training Program and its replacement, the Employment for Skill Development system planned from April 2027. Covers the Japanese you need on arrival, workplace culture, and the rules and rights every worker should know.',
      ja: '技能実習制度と、2027年4月からの導入が予定されている育成就労制度で日本へ行く方のための集中研修です。到着後すぐに必要な日本語、職場文化、働く人が知っておくべきルールと権利を学びます。',
    },
    learn: { en: ['Survival and workplace Japanese', 'Japanese work culture and manners', 'Housing, banking, transport and garbage rules', 'Your rights, support and who to contact'], ja: ['生活と職場の日本語', '日本の仕事文化とマナー', '住まい・銀行・交通・ゴミ出しのルール', '自分の権利と相談先'] },
    forWho: { en: ['Selected trainees before departure', 'Candidates waiting for matching', 'Families who want to understand the process'], ja: ['出国前の実習生', 'マッチング待ちの候補者', '流れを知りたいご家族'] },
    outcome: { en: 'Arrive in Japan ready for work and daily life.', ja: '仕事と生活の準備を整えて日本に到着できます。' },
  },
  {
    slug: 'interview-workplace',
    category: 'career',
    icon: 'UserCheck',
    kanji: '面',
    title: { en: 'Interview & Workplace Etiquette', ja: '面接・ビジネスマナー' },
    jpName: '面接対策・ビジネスマナー',
    short: { en: 'Japanese CV, interview practice and workplace manners.', ja: '日本語の履歴書・面接練習・職場のマナー。' },
    level: { en: 'N5 and up', ja: 'N5以上' },
    duration: { en: 'About 4 weeks', ja: '約4週間' },
    overview: {
      en: 'Many good candidates lose the job at the interview. We practise real interview questions with Japanese instructors, write your rirekisho (Japanese CV), and teach the manners Japanese employers notice: bowing, punctuality, keigo and teamwork.',
      ja: '多くの優秀な候補者が面接で不合格になります。日本人講師と実際の質問で練習し、履歴書を作成し、お辞儀・時間厳守・敬語・チームワークなど日本の企業が見るマナーを学びます。',
    },
    learn: { en: ['Rirekisho (Japanese CV) writing', 'Mock interviews, online and in person', 'Keigo (polite language) basics', 'Workplace rules: hou-ren-sou, punctuality, 5S'], ja: ['履歴書の書き方', '対面・オンラインの模擬面接', '敬語の基本', '報連相・時間厳守・5Sなど職場のルール'] },
    forWho: { en: ['SSW and TITP candidates', 'Graduates applying to Japanese companies', 'Anyone with an upcoming interview'], ja: ['特定技能・技能実習の候補者', '日本企業に応募する卒業生', '面接を控えている方'] },
    outcome: { en: 'Walk into a Japanese interview prepared and confident.', ja: '準備万端で自信を持って面接に臨めます。' },
  },

  // ---------------- Business & professional
  {
    slug: 'business-japanese',
    category: 'professional',
    icon: 'Building2',
    kanji: '商',
    title: { en: 'Business Japanese & BJT', ja: 'ビジネス日本語・BJT対策' },
    jpName: 'ビジネス日本語',
    short: { en: 'Keigo, emails, meetings and the BJT business test.', ja: '敬語・メール・会議とBJTビジネス日本語能力テスト。' },
    level: { en: 'N4 and up', ja: 'N4以上' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'For professionals who work with Japanese clients or companies. Learn the polite language, email formats and meeting phrases Japanese business runs on, and prepare for the BJT Business Japanese Proficiency Test.',
      ja: '日本の顧客や企業と仕事をする方向け。日本のビジネスに欠かせない敬語、メールの書き方、会議での表現を学び、BJTビジネス日本語能力テストに備えます。',
    },
    learn: { en: ['Keigo: sonkeigo, kenjougo, teineigo', 'Business emails and documents', 'Meetings, calls and presentations', 'BJT practice questions'], ja: ['敬語：尊敬語・謙譲語・丁寧語', 'ビジネスメールと文書', '会議・電話・プレゼン', 'BJTの問題演習'] },
    forWho: { en: ['Professionals working with Japan', 'Company teams (see Business programme)', 'N4+ learners aiming for office jobs'], ja: ['日本と仕事をする社会人', '企業チーム（法人プログラム参照）', '事務職を目指すN4以上の方'] },
    outcome: { en: 'Communicate professionally with Japanese colleagues and clients.', ja: '日本の同僚や顧客とプロとしてやり取りできます。' },
  },
  {
    slug: 'it-japanese',
    category: 'professional',
    icon: 'Code2',
    kanji: 'IT',
    title: { en: 'Japanese for IT Engineers', ja: 'ITエンジニアのための日本語' },
    jpName: 'IT日本語',
    short: { en: 'Japanese for software teams: specs, stand-ups and code reviews.', ja: '仕様書・朝会・コードレビューのための日本語。' },
    level: { en: 'N4 and up', ja: 'N4以上' },
    duration: { en: 'About 3 months', ja: '約3か月' },
    overview: {
      en: 'Japan has a large shortage of IT engineers. This course gives developers the Japanese they need to join a Japanese team: reading specifications, reporting progress, joining stand-ups and discussing code.',
      ja: '日本ではITエンジニアが大きく不足しています。仕様書を読む、進捗を報告する、朝会に参加する、コードについて話し合うなど、日本のチームに入るための日本語を学びます。',
    },
    learn: { en: ['IT vocabulary and katakana terms', 'Reading specs and tickets', 'Stand-ups, reports and estimates', 'Interviews with Japanese IT companies'], ja: ['IT用語とカタカナ語', '仕様書・チケットを読む', '朝会・報告・見積もり', '日本のIT企業の面接'] },
    forWho: { en: ['Software developers and IT graduates', 'Engineers offshoring for Japanese clients', 'Students in IT programmes'], ja: ['ソフトウェア開発者・IT系の卒業生', '日本向けオフショア開発のエンジニア', 'IT専攻の学生'] },
    outcome: { en: 'Work effectively in a Japanese-speaking software team.', ja: '日本語で開発チームの一員として働けます。' },
  },
  {
    slug: 'translation-interpretation',
    category: 'professional',
    icon: 'ArrowLeftRight',
    kanji: '訳',
    title: { en: 'Translation & Interpretation', ja: '翻訳・通訳コース' },
    jpName: '翻訳・通訳',
    short: { en: 'Japanese ↔ English / Urdu translation and interpreting skills.', ja: '日本語⇔英語・ウルドゥー語の翻訳・通訳スキル。' },
    level: { en: 'N3 and up', ja: 'N3以上' },
    duration: { en: 'About 4 months', ja: '約4か月' },
    overview: {
      en: 'For advanced learners who want to use Japanese professionally. Practise translating documents and interpreting in meetings, factory visits and interviews between Japanese, English and Urdu.',
      ja: '日本語を仕事に生かしたい上級者向け。日本語・英語・ウルドゥー語の間で、文書翻訳や会議・工場見学・面接での通訳を練習します。',
    },
    learn: { en: ['Document translation techniques', 'Consecutive interpreting', 'Business and technical glossaries', 'Ethics and accuracy for interpreters'], ja: ['文書翻訳の技術', '逐次通訳', 'ビジネス・技術用語集', '通訳者の倫理と正確さ'] },
    forWho: { en: ['N3–N1 learners', 'Staff working with Japanese partners', 'Future freelance translators'], ja: ['N3〜N1の学習者', '日本のパートナーと働く社員', 'フリーランス翻訳者を目指す方'] },
    outcome: { en: 'Translate and interpret accurately in professional settings.', ja: '仕事の場で正確に翻訳・通訳できます。' },
  },

  // ---------------- Kids & culture
  {
    slug: 'japanese-for-kids',
    category: 'kids',
    icon: 'Smile',
    kanji: '子',
    title: { en: 'Japanese for Kids (ages 9–12)', ja: 'キッズ日本語（9〜12歳）' },
    jpName: 'こども日本語',
    short: { en: 'Fun, game-based Japanese with 1-year and 2-year plans.', ja: 'ゲームで楽しく学ぶ、1年・2年プラン。' },
    level: { en: 'Ages 9–12', ja: '9〜12歳' },
    duration: { en: '1-year or 2-year plan', ja: '1年または2年プラン' },
    overview: {
      en: 'Children learn Japanese through songs, games, origami and stories, with progress reports for parents. Choose a 1-year or 2-year plan, or ask for a custom plan for your child.',
      ja: '歌・ゲーム・折り紙・おはなしで日本語を学び、保護者には進捗レポートをお届けします。1年・2年プランのほか、お子さまに合わせたカスタムプランも相談できます。',
    },
    learn: { en: ['Hiragana and katakana through games', 'Speaking and singing in Japanese', 'Origami, calligraphy and festivals', 'Confidence and good manners'], ja: ['ゲームでひらがな・カタカナ', '日本語で話す・歌う', '折り紙・書道・お祭り', '自信と礼儀'] },
    forWho: { en: ['Children aged 9–12', 'Parents planning a future in Japan', 'Kids who love anime and Japan'], ja: ['9〜12歳のお子さま', '将来日本を考えるご家庭', 'アニメや日本が好きな子'] },
    outcome: { en: 'A strong, happy start in Japanese that grows with your child.', ja: 'お子さまと一緒に成長する、楽しい日本語のスタート。' },
    featured: true,
  },
  {
    slug: 'culture-workshops',
    category: 'kids',
    icon: 'Flower2',
    kanji: '和',
    title: { en: 'Japanese Culture Workshops', ja: '日本文化ワークショップ' },
    jpName: '日本文化体験',
    short: { en: 'Calligraphy, origami, cuisine, movie days and festivals.', ja: '書道・折り紙・料理・映画の日・お祭り。' },
    level: { en: 'Everyone', ja: 'どなたでも' },
    duration: { en: 'Workshops through the year', ja: '年間を通して開催' },
    overview: {
      en: 'Language is only half of living in Japan. Our workshops bring the culture to Lahore: shodo calligraphy, origami, Japanese cooking, movie days and the summer festival. Open to students, families and friends of Japan.',
      ja: '日本で暮らすには、言葉だけでなく文化も大切です。書道、折り紙、日本料理、映画の日、夏祭りなど、ラホールで日本文化を体験できます。受講生、ご家族、日本が好きな方ならどなたでも。',
    },
    learn: { en: ['Shodo (calligraphy) and origami', 'Japanese cooking basics', 'Seasons, festivals and etiquette', 'Movie days with discussion'], ja: ['書道と折り紙', '日本料理の基本', '季節・行事・礼儀作法', '映画の日とディスカッション'] },
    forWho: { en: ['NCP students and their families', 'Anyone who loves Japanese culture', 'Schools and groups'], ja: ['NCPの受講生とご家族', '日本文化が好きな方', '学校・団体'] },
    outcome: { en: 'Understand Japan beyond the textbook.', ja: '教科書を超えて日本を理解できます。' },
  },
]

export const coursesByCategory = (id: CategoryId) => COURSES.filter((c) => c.category === id)
export const getCourse = (slug: string) => COURSES.find((c) => c.slug === slug)
export const coursePath = (locale: Locale, slug: string) => `/${locale}/courses/${slug}`
